import { useEffect, useRef, useCallback } from 'react'
import type { FC } from 'react'
import { WorksheetHeader } from './WorksheetHeader'
import { TabNavigation } from './TabNavigation'
import { RhymeSearchTab } from './RhymeSearchTab'
import { ThesaurusSearchTab } from './ThesaurusSearchTab'

interface ExplorationWorksheetProps {
  isOpen: boolean
  searchTerm: string
  activeTab: 'rhymes' | 'thesaurus'
  rhythmicStability?: number
  parentWordId?: string | null
  searchNonce?: number
  onClose: () => void
  onSearchChange: (term: string) => void
  onTabChange: (tab: 'rhymes' | 'thesaurus') => void
  closeOnBackdrop?: boolean
  /** Called when user wants to collect word and switch to rhymes tab */
  onCollectAndExploreRhymes?: (word: string) => void
}

/**
 * Right-side slide-over panel for exploring rhymes and thesaurus
 * Provides search, tab navigation, and content workspace for Layer-2 features
 */
export const ExplorationWorksheet: FC<ExplorationWorksheetProps> = ({
  isOpen,
  searchTerm,
  activeTab,
  rhythmicStability,
  parentWordId: _parentWordId,
  searchNonce,
  onClose,
  onSearchChange,
  onTabChange,
  closeOnBackdrop = true,
  onCollectAndExploreRhymes
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(false)

  // Handle focus management on open/close transitions
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Opening: capture previous focus and move to panel
      previousFocusRef.current = document.activeElement as HTMLElement
      if (panelRef.current) {
        panelRef.current.focus()
      }
    } else if (!isOpen && wasOpenRef.current) {
      // Closing: restore focus to previous element
      if (previousFocusRef.current) {
        previousFocusRef.current.focus()
      }
    }
    wasOpenRef.current = isOpen
  }, [isOpen])

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Focus trap implementation
  useEffect(() => {
    if (!isOpen || !panelRef.current) return

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusableElements = panelRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )

      if (!focusableElements || focusableElements.length === 0) return

      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }

    panelRef.current.addEventListener('keydown', handleTab)
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      panelRef.current?.removeEventListener('keydown', handleTab)
    }
  }, [isOpen])

  const handleBackdropClick = useCallback(() => {
    if (closeOnBackdrop) {
      onClose()
    }
  }, [closeOnBackdrop, onClose])

  const handleClearSearch = useCallback(() => {
    onSearchChange('')
  }, [onSearchChange])

  // Keep the worksheet mounted but hidden when closed to preserve thesaurus cache
  // This prevents re-fetching when the user reopens with the same search term

  return (
    <div
      className={isOpen ? '' : 'hidden'}
      aria-hidden={!isOpen}
    >
      {/* Backdrop */}
      <div
        data-testid="worksheet-backdrop"
        className="fixed inset-0 bg-black/20 z-40"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        data-testid="exploration-worksheet"
        className={`fixed top-0 right-0 h-full w-full max-w-[400px] bg-white shadow-2xl z-50 flex flex-col ${isOpen ? 'animate-slide-in-right' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="worksheet-title"
        tabIndex={-1}
      >
        {/* Header with close button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 id="worksheet-title" className="text-lg font-semibold text-gray-900">
            Word Explorer
          </h2>
          <button
            data-testid="worksheet-close-button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close worksheet"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Search Header */}
        <WorksheetHeader
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          onClear={handleClearSearch}
          rhythmicStability={rhythmicStability}
        />

        {/* Tab Navigation */}
        <TabNavigation activeTab={activeTab} onTabChange={onTabChange} />

        {/* Content Area */}
        <div
          data-testid="worksheet-content"
          className="flex-1 overflow-y-auto bg-gray-50"
        >
          {activeTab === 'rhymes' ? (
            <div className="p-4">
              <RhymeSearchTab
                searchTerm={searchTerm}
                rhythmicStability={rhythmicStability}
                searchNonce={searchNonce}
                onClear={handleClearSearch}
              />
            </div>
          ) : (
            <ThesaurusSearchTab
              searchTerm={searchTerm}
              onClear={handleClearSearch}
              onCollectAndExploreRhymes={onCollectAndExploreRhymes}
            />
          )}
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        .animate-slide-in-right {
          animation: slide-in-right 200ms ease-out;
        }
      `}</style>
    </div>
  )
}
